import { Link } from 'react-router';
import { useState, useEffect } from 'react';
import { SEO } from '@/components/seo/seo';

export function NotFoundPage() {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <>
      <SEO
        title="Page Not Found"
        description="The requested Fixtyer page could not be found. Browse our pothole claim guidance or return to the homepage."
        noindex
      />
      <div className="relative min-h-[calc(100vh-8rem)] overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-950 via-brand-900 to-brand-950">
        {/* Floating orbs that follow mouse slightly */}
        <div 
          className="absolute w-96 h-96 bg-accent-500/20 rounded-full blur-3xl transition-transform duration-1000 ease-out"
          style={{
            left: `calc(20% + ${mousePos.x * 0.02}px)`,
            top: `calc(30% + ${mousePos.y * 0.02}px)`,
          }}
        />
        <div 
          className="absolute w-64 h-64 bg-brand-500/30 rounded-full blur-3xl transition-transform duration-1000 ease-out"
          style={{
            right: `calc(20% - ${mousePos.x * 0.015}px)`,
            bottom: `calc(20% - ${mousePos.y * 0.015}px)`,
          }}
        />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="404grid" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#404grid)" />
          </svg>
        </div>
      </div>

      {/* Content */}
      <div className="relative flex flex-col items-center justify-center min-h-[calc(100vh-8rem)] px-4 py-16">
        {/* Animated 404 */}
        <div className="relative mb-8">
          {/* Pothole SVG illustration */}
          <svg 
            viewBox="0 0 400 200" 
            className="w-80 md:w-96 h-auto"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Road */}
            <rect x="0" y="100" width="400" height="100" fill="#374151" />
            <rect x="0" y="95" width="400" height="8" fill="#4B5563" />
            
            {/* Road markings */}
            <rect x="20" y="145" width="60" height="8" rx="2" fill="#fbbf24" />
            <rect x="120" y="145" width="60" height="8" rx="2" fill="#fbbf24" />
            <rect x="320" y="145" width="60" height="8" rx="2" fill="#fbbf24" />
            
            {/* Pothole */}
            <ellipse cx="250" cy="150" rx="45" ry="25" fill="#1f2937" />
            <ellipse cx="250" cy="150" rx="35" ry="18" fill="#111827" />
            <ellipse cx="250" cy="148" rx="25" ry="10" fill="#0f172a" />
            
            {/* 4 */}
            <text x="60" y="80" className="fill-white font-display" fontSize="80" fontWeight="bold">4</text>
            
            {/* 0 as pothole warning sign */}
            <g transform="translate(145, 10)">
              <polygon points="55,0 110,95 0,95" fill="#f97316" />
              <polygon points="55,10 100,85 10,85" fill="#1e40af" />
              <text x="55" y="70" textAnchor="middle" className="fill-white font-display" fontSize="36" fontWeight="bold">!</text>
            </g>
            
            {/* 4 */}
            <text x="280" y="80" className="fill-white font-display" fontSize="80" fontWeight="bold">4</text>
          </svg>
        </div>

        {/* Message */}
        <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white text-center mb-4">
          Looks like you hit a{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-400 to-accent-500">
            dead end
          </span>
        </h1>
        
        <p className="text-brand-200 text-lg md:text-xl text-center max-w-lg mb-8">
          This page doesn't exist — but unlike potholes, we can't help you claim for this one.
        </p>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Link
            to="/"
            className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25 group"
          >
            <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Back to Home
          </Link>
          <Link
            to="/guides"
            className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transition-all"
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            Browse Guides
          </Link>
        </div>

        {/* Helpful links */}
        <div className="mt-16 text-center">
          <p className="text-brand-400 text-sm mb-4">Looking for something specific?</p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <Link to="/how-it-works" className="text-brand-300 hover:text-white transition-colors">
              How It Works
            </Link>
            <span className="text-brand-600">•</span>
            <Link to="/pricing" className="text-brand-300 hover:text-white transition-colors">
              Pricing
            </Link>
            <span className="text-brand-600">•</span>
            <Link to="/faq" className="text-brand-300 hover:text-white transition-colors">
              FAQs
            </Link>
            <span className="text-brand-600">•</span>
            <Link to="/check" className="text-brand-300 hover:text-white transition-colors">
              Start a Claim
            </Link>
          </div>
        </div>
      </div>

      {/* Diagonal stripe at bottom */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-8"
        style={{
          background: `repeating-linear-gradient(
            -45deg,
            #1e40af,
            #1e40af 40px,
            #f97316 40px,
            #f97316 80px
          )`
        }}
      />
      </div>
    </>
  );
}
